/** Namespace `billing` (Belege/Rechnungen, Katalog, Einstellungen, Onboarding) — befüllt während der Migration. */
export const billing = {
  de: {
    billing: {
      // Generic / shared
      eyebrow: 'Abrechnung',
      loading: 'Lade…',
      loadError: 'Belege konnten nicht geladen werden',
      saveFailed: 'Speichern fehlgeschlagen',
      deleteFailed: 'Löschen fehlgeschlagen',
      sortFailed: 'Sortierung fehlgeschlagen',
      actionFailed: 'Aktion fehlgeschlagen',
      pdfLoadError: 'PDF konnte nicht geladen werden',
      filterReset: 'Filter zurücksetzen',
      noMatches: 'Keine Treffer',
      back: 'Zurück',
      cancel: 'Abbrechen',
      save: 'Speichern',
      saving: 'Speichere…',
      create: 'Anlegen',

      // Status labels
      status: {
        draft: 'Entwurf',
        sent: 'Versendet',
        accepted: 'Angenommen',
        rejected: 'Abgelehnt',
        issued: 'Ausgestellt',
        paid: 'Bezahlt',
        canceled: 'Storniert',
      },

      // Type labels
      type: {
        offer: 'Angebot',
        invoice: 'Rechnung',
        cancellation: 'Stornorechnung',
      },

      // Language labels
      lang: {
        de: 'Deutsch',
        es: 'Español',
        en: 'English',
        ru: 'Русский',
      },

      // Category labels
      category: {
        implantology: 'Implantologie',
        prophylaxis: 'Prophylaxe',
        aesthetic: 'Ästhetik',
        oral_surgery: 'Chirurgie',
        orthodontics: 'KFO',
        orthodonticsLong: 'Kieferorthopädie',
        consultation: 'Beratung',
        other: 'Sonstige',
      },

      // ---- Invoice list ----
      list: {
        title: 'Belege',
        description: 'Angebote, Rechnungen und Stornobelege — fortlaufend nummeriert nach Festschreibung',
        countSuffix: 'Belege',
        open: 'offen',
        overdue: 'überfällig',
        catalog: 'Katalog',
        catalogTitle: 'Leistungskatalog verwalten',
        offer: 'Angebot',
        invoice: 'Rechnung',
        tabAll: 'Alle',
        tabOffers: 'Angebote',
        tabInvoices: 'Rechnungen',
        tabCancellations: 'Storno',
        tabTypesLabel: 'Belegtypen',
        searchLabel: 'Suche (Nummer, Patient, Stadt)',
        searchPlaceholder: 'z. B. RE-0042, Müller, Palma',
        statusLabel: 'Status',
        statusAll: 'Alle',
        fromLabel: 'Von',
        toLabel: 'Bis',
        emptyTitle: 'Noch keine Belege',
        emptyDescription:
          'Belege werden meist direkt aus einem Termin oder der Patientenakte erstellt — alternativ rechts oben anlegen.',
        emptyFilterDescription: 'Keine Belege entsprechen den aktuellen Filtern.',
        colNrType: 'Nr. / Typ',
        colPatient: 'Patient',
        colDate: 'Datum',
        colStatus: 'Status',
        colAmount: 'Betrag',
        colAction: 'Aktion',
        due: 'fällig {{date}}',
        validUntil: 'gültig bis {{date}}',
        pdfOpen: 'PDF öffnen',
        openArrow: 'Öffnen →',
        footerCount: '{{count}} Belege im aktuellen Filter',
        sum: 'Summe:',
        // Help banner
        helpTitle: 'Wie funktionieren Belege?',
        helpSubtitle: 'Angebot, Rechnung und Stornorechnung — kurz erklärt, mit interaktiver Tour.',
        startTour: 'Tour starten',
        helpCardOfferTitle: 'Angebot',
        helpCardOfferText:
          'Kostenvoranschlag vor der Behandlung — vom Patienten annehmen lassen, dann in eine Rechnung umwandeln.',
        helpCardInvoiceTitle: 'Rechnung',
        helpCardInvoiceText:
          'Hauptpfad: Termin abschließen, Quick-Chips klicken, ausstellen und per Mail senden.',
        helpCardCancellationTitle: 'Stornorechnung',
        helpCardCancellationText:
          'Korrekturen an festgeschriebenen Rechnungen — Pflicht-Workflow fürs Finanzamt.',
        helpTip: 'Tipp: Die Tour kannst du jederzeit über',
        helpTipBold: 'Tour starten',
        helpTipSuffix: 'oben rechts erneut öffnen.',
        helpMinutes: '~2 min',
      },

      // ---- Catalog page ----
      catalog: {
        title: 'Leistungskatalog',
        description:
          'Wiederverwendbare Positionsvorlagen. Werden im Editor und im „Behandlung abschließen"-Dialog angeboten.',
        backToInvoices: 'Belege',
        newItem: 'Neuer Eintrag',
        searchLabel: 'Suche',
        searchPlaceholder: 'Code oder Bezeichnung…',
        categoryLabel: 'Kategorie',
        categoryAll: 'Alle',
        visibilityLabel: 'Sichtbarkeit',
        visibilityActive: 'Nur aktive',
        visibilityAll: 'Alle (inkl. inaktiv)',
        emptyTitle: 'Noch keine Katalogeinträge',
        emptyFilterTitle: 'Keine Treffer',
        emptyDescription:
          'Lege wiederkehrende Leistungen einmal an — sie sind dann per 1-Klick im Beleg-Editor verfügbar.',
        emptyFilterDescription: 'Keine Einträge entsprechen den aktuellen Filtern.',
        emptyAction: 'Ersten Eintrag anlegen',
        colSort: 'Sort.',
        colName: 'Bezeichnung',
        colCodeCategory: 'Code · Kategorie',
        colPriceVat: 'Preis · MwSt',
        colVisible: 'Sichtbar',
        colAction: 'Aktion',
        moveUp: 'Nach oben',
        moveDown: 'Nach unten',
        unnamed: 'unbenannt',
        vatShort: 'MwSt {{rate}}%',
        active: 'aktiv',
        inactive: 'inaktiv',
        loadError: 'Katalog konnte nicht geladen werden',
        itemUpdated: 'Katalogeintrag aktualisiert',
        itemCreated: 'Katalogeintrag angelegt',
        itemDeleted: 'Eintrag gelöscht',
        confirmDelete: 'Katalogeintrag „{{name}}" wirklich löschen? Bestehende Belege bleiben unverändert.',
        // Editor modal
        editorEditTitle: 'Katalogeintrag bearbeiten',
        editorNewTitle: 'Neuer Katalogeintrag',
        codeLabel: 'Code (intern, optional)',
        codePlaceholder: 'z. B. IMP-STR',
        statusLabel: 'Status',
        statusActive: 'Aktiv',
        statusInactive: 'Inaktiv (nicht im Picker)',
        nameSection: 'Bezeichnung (mindestens eine Sprache)',
        descriptionSection: 'Beschreibung (optional)',
        unitLabel: 'Einheit',
        priceLabel: 'Preis (Default)',
        vatLabel: 'MwSt % (Default)',
        sortOrderLabel: 'Sortierung',
      },

      // ---- Catalog picker (modal) ----
      picker: {
        title: 'Katalog durchsuchen',
        categoryAll: 'Alle Kategorien',
        loading: 'Lade Katalog…',
        empty: 'Katalog ist leer — bitte zuerst Einträge anlegen (Einstellungen → Rechnungsstellung → Katalog).',
        noMatches: 'Keine Treffer. Anderes Stichwort versuchen oder „Freie Position" anlegen.',
        unnamed: 'Unbenannt',
        vatShort: 'MwSt {{rate}}%',
        searchPlaceholder: 'Code oder Bezeichnung…',
      },

      // ---- Invoice editor ----
      editor: {
        loadError: 'Beleg konnte nicht geladen werden',
        createError: 'Beleg konnte nicht angelegt werden. Bitte Patient erneut wählen.',
        patientNotFound:
          'Patient mit ID {{id}} wurde nicht gefunden. Wähle einen anderen oder lege neu an.',
        saved: 'Beleg gespeichert',
        itemAddError: 'Position konnte nicht hinzugefügt werden',
        itemSaveError: 'Position konnte nicht gespeichert werden',
        freeItemLabel: 'Freie Position',
        confirmDeleteItem: 'Position „{{label}}" löschen?',
        itemWithoutLabel: 'ohne Bezeichnung',
        confirmFinalizeDirty: 'Es gibt ungespeicherte Änderungen am Beleg. Trotzdem festschreiben?',
        finalizedAs: 'Festgeschrieben als {{number}}',
        markedPaid: 'Als bezahlt markiert',
        cancelReasonPrompt: 'Stornogrund (wird auf der Stornorechnung vermerkt):',
        cancellationCreated: 'Stornorechnung {{number}} erstellt',
        invoiceDraftCreated: 'Rechnungsentwurf erstellt',
        confirmDiscardDraft:
          'Diesen Entwurf wirklich verwerfen? Das kann nicht rückgängig gemacht werden.',
        draftDeleted: 'Entwurf gelöscht',
        notFound: 'Beleg nicht gefunden.',
        // New-doc stub
        newDocTitle: 'Neuer Beleg ({{type}})',
        newDocDescription: 'Wähle zuerst den Patienten — Anschrift und Sprache werden übernommen.',
        patientLabel: 'Patient',
        pickPatientTip:
          'Tipp: Wenn der gesuchte Patient nicht erscheint, lege ihn direkt unten im Dropdown an — Adresse und Sprache lassen sich später nachpflegen.',
        // Header
        finalizedDescription:
          'Festgeschrieben am {{date}} — Änderungen nur über Storno.',
        editorDescription:
          'Editor — Positionen, Adresse und Texte. Speichern bestätigt Änderungen, Festschreiben vergibt die Nummer.',
        pdf: 'PDF',
        preview: 'Vorschau',
        listLink: 'Liste',
        finalizedBannerTitle: 'Beleg ist festgeschrieben',
        finalizedBannerText:
          'Eingaben sind gesperrt. Bei Bedarf eine Stornorechnung erzeugen und neu ausstellen.',
        viewOnlyBannerTitle: 'Nur-Lese-Ansicht',
        viewOnlyBannerText:
          'Du kannst diesen Beleg ansehen, aber nicht bearbeiten. Dafür fehlt die Berechtigung „Belege erstellen/bearbeiten".',
        // Recipient
        recipientTitle: 'Empfänger (Snapshot)',
        nameCompany: 'Name / Firma',
        nieNif: 'NIE / NIF / USt-ID',
        street: 'Straße',
        postalCode: 'PLZ',
        country: 'Land',
        city: 'Stadt',
        email: 'E-Mail',
        recipientHint:
          'Anschrift wird auf der Rechnung eingefroren. Änderungen hier wirken sich nicht auf die Patientenstammdaten aus.',
        // Header note
        headerNoteLabel: 'Kopftext (oberhalb der Tabelle)',
        headerNotePlaceholder: 'z. B. "Behandlung vom 13.05.2026"',
        // Items
        itemsTitle: 'Positionen ({{count}})',
        fromCatalog: 'Aus Katalog',
        freeItem: 'Freie Position',
        itemsEmptyTitle: 'Noch keine Positionen',
        itemsEmptyReadonly: 'Festgeschriebener Beleg ohne Positionen — nicht editierbar.',
        itemsEmptyEditable: 'Wähle eine Vorlage aus dem Katalog oder füge eine freie Position hinzu.',
        // Items table
        colNr: '#',
        colLabel: 'Bezeichnung',
        colQuantity: 'Menge',
        colUnit: 'Einheit',
        colUnitPrice: 'Einzelpreis',
        colVat: 'MwSt %',
        colDiscount: 'Rabatt %',
        colTotal: 'Gesamt',
        labelPlaceholder: 'Bezeichnung der Leistung',
        descriptionPlaceholder: 'Optionaler Zusatztext',
        moveUp: 'Nach oben',
        moveDown: 'Nach unten',
        moveItemUp: 'Position nach oben verschieben',
        moveItemDown: 'Position nach unten verschieben',
        deleteItem: 'Position löschen',
        // Footer texts
        paymentTerms: 'Zahlungsbedingungen',
        bankDetails: 'Bankverbindung (überschreibt Settings)',
        bankDetailsPlaceholder: 'Leer = Standardtext aus Einstellungen',
        footerText: 'Fußtext (z. B. „Vielen Dank…")',
        internalNote: 'Interne Notiz (nicht auf Rechnung sichtbar)',
        // Meta
        metaTitle: 'Stammdaten',
        patientFile: 'Akte →',
        language: 'Sprache',
        date: 'Datum',
        invoiceDate: 'Rechnungsdatum',
        dueDate: 'Fällig am',
        validUntilLabel: 'Gültig bis',
        createdFromOffer: 'Erzeugt aus',
        offerHash: 'Angebot #{{id}}',
        cancelsInvoice: 'Storniert',
        invoiceHash: 'Rechnung #{{id}}',
        canceledBy: 'Storniert durch',
        cancellationHash: 'Stornorechnung #{{id}}',
        // Totals
        totalsTitle: 'Summen',
        net: 'Netto',
        vatRate: 'MwSt {{rate}}%',
        total: 'Gesamt',
        // Actions
        saveChanges: 'Änderungen speichern',
        savedLabel: 'Gespeichert',
        savingLabel: 'Speichere…',
        finalize: 'Festschreiben',
        finalizing: 'Schreibe fest…',
        finalizeDisabledTitle: 'Mindestens eine Position erforderlich',
        discardDraft: 'Entwurf verwerfen',
        offerAccepted: 'Angebot angenommen',
        offerRejected: 'Abgelehnt',
        convertToInvoice: 'In Rechnung umwandeln',
        markPaid: 'Als bezahlt markieren',
        cancelInvoice: 'Stornieren',
        sendByMail: 'Per Mail senden',
        downloadPdf: 'PDF herunterladen',
      },

      // ---- Completion dialog ----
      completion: {
        prepareError: 'Vorbereitung fehlgeschlagen',
        title: 'Behandlung abschließen',
        seedItemLabel: 'Behandlung',
        seedItemUnit: 'Sitzung',
        patientLinked: 'Patient {{name}} verknüpft',
        linkError: 'Verknüpfung fehlgeschlagen',
        noPatientError: 'Termin ohne verknüpften Patienten — bitte zuerst Patient verknüpfen.',
        noItemsError: 'Mindestens eine Position mit Bezeichnung erforderlich.',
        treatmentFrom: 'Behandlung vom {{date}}.',
        completedNoInvoice: 'Termin abgeschlossen (ohne Rechnung)',
        draftCreated: 'Rechnungsentwurf erstellt',
        invoiceIssued: 'Rechnung {{number}} ausgestellt',
        // Patient link banner
        noPatientTitle: 'Kein Patient verknüpft',
        noPatientText:
          'Damit eine Rechnung erstellt werden kann, muss der Termin einem Patientendatensatz zugeordnet werden. Alternativ kannst du den Termin ohne Rechnung abschließen.',
        linkPatient: 'Patient verknüpfen',
        patientSearchPlaceholder: 'Name, E-Mail, Telefon oder Patientennummer…',
        searching: 'Suche…',
        noMatches: 'Keine Treffer.',
        linking: 'Verknüpfe…',
        patientNotListed: 'Patient nicht dabei?',
        createInFile: 'In der Patientenakte anlegen',
        createInFileSuffix: 'und danach erneut abschließen.',
        // Items
        servicesTitle: 'Erbrachte Leistungen ({{count}})',
        itemsEmpty: 'Noch keine Position. Wähle unten aus dem Katalog oder lege eine freie Position an.',
        colLabel: 'Bezeichnung',
        colQuantity: 'Mng.',
        colUnitPrice: 'Einzelpreis',
        colVat: 'MwSt%',
        colTotal: 'Gesamt',
        servicePlaceholder: 'Leistung',
        removeItem: 'Position entfernen',
        // Quick chips
        quickTitle: 'Häufige Leistungen (1 Klick = hinzufügen)',
        quickHint:
          'Tipp: In Einstellungen → Rechnungsstellung → Schnellauswahl lassen sich bis zu 8 häufige Leistungen als 1-Klick-Buttons hinterlegen.',
        searchCatalog: 'Aus Katalog suchen',
        freeItem: 'Freie Position',
        catalogSearchPlaceholder: 'Code oder Bezeichnung…',
        noMatchesCatalog: 'Keine Treffer.',
        // Internal note
        internalNoteLabel: 'Notiz (intern, nicht auf Rechnung sichtbar)',
        internalNotePlaceholder: 'z. B. Patient wünscht Anpassung der Krone in 4 Wochen',
        // Totals footer
        languageLabel: 'Sprache:',
        appointmentStatus: 'Status Termin:',
        net: 'Netto',
        vat: 'MwSt',
        // Actions
        withoutInvoice: 'Ohne Rechnung',
        asDraft: 'Als Entwurf',
        issueNow: 'Direkt ausstellen',
        savingDraft: 'Speichere…',
        issuing: 'Stelle aus…',
        patientRequired: 'Patient muss verknüpft sein',
        itemLabelRequired: 'Mindestens eine Position mit Bezeichnung erforderlich',
      },

      // ---- Send invoice dialog ----
      send: {
        previewError: 'Vorlage konnte nicht geladen werden',
        recipientSubjectRequired: 'Empfänger und Betreff sind Pflicht',
        sent: 'E-Mail versendet',
        sendFailed: 'Versand fehlgeschlagen',
        title: 'Beleg per E-Mail senden',
        loadingTemplate: 'Lade Vorlage…',
        toLabel: 'An',
        toPlaceholder: 'patient@example.com',
        languageLabel: 'Sprache',
        subjectLabel: 'Betreff',
        bodyLabel: 'Nachricht',
        attachment: 'Anhang:',
        attachmentNote: '(eingefrorenes Original)',
        reloadTemplate: 'Vorlage neu laden',
        send: 'Senden',
        sending: 'Sende…',
      },

      // ---- Patient picker ----
      patientPicker: {
        change: 'Ändern',
        cancel: 'Abbrechen',
        searchPlaceholder: 'Patient suchen (Name, E-Mail, NIE, Telefon)…',
        minChars: 'Mindestens 2 Zeichen eingeben oder neuen Patienten anlegen.',
        searching: 'Suche…',
        noMatches: 'Keine Treffer für „{{query}}".',
        createNew: 'Neuen Patienten anlegen',
        // Create modal
        newPatient: 'Neuer Patient',
        firstName: 'Vorname',
        lastName: 'Nachname',
        emailLabel: 'E-Mail',
        phoneLabel: 'Telefon',
        languageLabel: 'Sprache',
        optional: 'optional',
        patientCreated: 'Patient {{number}} angelegt',
        createError: 'Patient konnte nicht angelegt werden',
        addressLaterHint: 'Adresse und NIE können später in der Patientenakte ergänzt werden.',
        createAndUse: 'Anlegen & verwenden',
        creating: 'Lege an…',
      },

      // ---- Billing settings ----
      settings: {
        loadError: 'Einstellungen konnten nicht geladen werden',
        saved: 'Einstellungen gespeichert',
        // SMTP section
        smtpTitle: 'Versand-Account für Rechnungen',
        smtpDescription:
          'Eigenes Postfach für Belege (Standard billing@seadent.es) — getrennt vom info@-Postfach der Buchungen. Bestätigt, dass Patientenantworten direkt im Buchhaltungs-Postfach landen.',
        smtpSaved: 'Versand-Account gespeichert',
        smtpRecipientMissing: 'Empfänger fehlt',
        smtpTestSent: 'Test-Mail an {{to}} versendet',
        smtpTestFailed: 'Test fehlgeschlagen',
        smtpPassSet: 'Passwort hinterlegt. Versand bereit.',
        smtpPassUnset:
          'Noch kein Passwort gesetzt — bitte unten eintragen und speichern. Erst danach funktioniert „Per Mail senden".',
        smtpDisplayName: 'Anzeigename (Absender)',
        smtpDisplayNamePlaceholder: 'SeaDent Mallorca · Billing',
        smtpEmail: 'E-Mail-Adresse',
        smtpHost: 'SMTP-Host',
        smtpPort: 'Port',
        smtpConnection: 'Verbindung',
        smtpStarttls: 'STARTTLS (Port 587)',
        smtpTls: 'Implicit TLS (Port 465)',
        smtpPassword: 'Passwort',
        smtpPasswordKeepHint: '(leer lassen, um bestehendes Passwort zu behalten)',
        smtpPasswordPlaceholder: 'Passwort vom Hosting-Anbieter',
        smtpSaveAccount: 'Account speichern',
        smtpTestPlaceholder: 'test@adresse.de',
        smtpTest: 'Test',
        smtpTesting: 'Sende…',
        smtpTestNeedsPass: 'Erst Passwort speichern',
        // Issuer
        issuerTitle: 'Klinik-Absenderdaten',
        issuerDescription:
          'Diese Angaben erscheinen oben auf jedem Beleg. Pflichtfelder für DACH/ES-Rechnungen: Firma, NIF/USt-Nr, Anschrift.',
        issuerName: 'Firmenname (inkl. Rechtsform)',
        issuerNamePlaceholder: 'z. B. SeaDent Mallorca SL',
        issuerLegalForm: 'Rechtsform (optional)',
        issuerLegalFormPlaceholder: 'SL / GmbH / …',
        issuerNif: 'NIF (Spanien)',
        issuerVatId: 'USt-IdNr (zusätzlich, optional)',
        issuerMedicalDirector: 'Ärztlicher Leiter (optional)',
        issuerAddress1: 'Adresszeile 1',
        issuerAddress2: 'Adresszeile 2 (optional)',
        issuerPostalCode: 'PLZ',
        issuerCity: 'Stadt',
        issuerCountry: 'Land (ISO 2)',
        issuerPhone: 'Telefon',
        issuerEmail: 'E-Mail',
        issuerWebsite: 'Webseite (optional)',
        issuerBank: 'Bank',
        issuerIban: 'IBAN',
        issuerBic: 'BIC',
        // Defaults
        defaultsTitle: 'Voreinstellungen für neue Belege',
        defaultsDescription: 'Werden beim Anlegen vorbefüllt — pro Beleg überschreibbar.',
        defaultVat: 'MwSt-Default',
        defaultDueDays: 'Zahlungsziel (Tage)',
        defaultOfferValidDays: 'Angebot gültig (Tage)',
        defaultLanguage: 'Standardsprache',
        defaultCurrency: 'Währung (ISO)',
        // Counters
        countersTitle: 'Nummernkreise',
        countersDescription:
          'Fortlaufende, getrennte Kreise. Die nächste Nummer kann bei der Erstmigration aus dem Altsystem gesetzt werden — danach besser nicht mehr anfassen.',
        counterUpdated: 'Nummernkreis {{type}} aktualisiert',
        counterUpdateError: 'Nummernkreis konnte nicht geändert werden',
        counterColType: 'Typ',
        counterColPrefix: 'Präfix',
        counterColPadding: 'Padding',
        counterColNext: 'Nächste Nr.',
        counterColPreview: 'Vorschau',
        // Texts
        textsTitle: 'Standardtexte je Sprache',
        textsDescription: 'Werden auf neuen Belegen vorbefüllt. Beim Einzelbeleg überschreibbar.',
        textPaymentTerms: 'Zahlungsbedingungen',
        textPaymentTermsHint: 'Erscheint unter den Summen bei Rechnungen.',
        textBankDetails: 'Bankverbindung (Override)',
        textBankDetailsHint: 'Leer = automatisch aus Issuer-Daten oben generieren.',
        textDefaultFooter: 'Standard-Fußtext',
        textDefaultFooterHint:
          'Vorschlag für den freundlichen Abschiedstext am Ende jeder Rechnung — pro Beleg editierbar.',
        textVatExemption: 'MwSt-Befreiungs-Hinweis',
        textVatExemptionHint: 'Wird angezeigt, sobald eine Position 0 % MwSt hat.',
        // Email templates
        emailTitle: 'E-Mail-Anschreiben für Angebote & Rechnungen',
        emailDescription:
          'Diese Texte werden beim Versand vorbefüllt. Pro Beleg lässt sich der Text im "Per Mail senden"-Dialog noch anpassen. Verfügbare Variablen: {{number}}, {{patient_name}}, {{total}}, {{due_date}}, {{valid_until}}, {{issuer_name}}.',
        emailSubject: 'Betreff',
        emailBody: 'Nachricht',
        // Quick chips
        quickTitle: 'Schnellauswahl im „Behandlung abschließen"-Dialog',
        quickDescription:
          'Bis zu 8 Katalogeinträge — werden im Termin-Workflow als 1-Klick-Buttons gezeigt, um häufige Leistungen blitzschnell zu erfassen.',
        quickCatalogEmpty: 'Der Katalog ist leer. Lege zuerst Einträge an —',
        quickOpenCatalog: 'Katalog öffnen',
        quickSelection: 'Auswahl ({{count}}/{{max}})',
        quickNothingSelected: 'Noch nichts gewählt. Rechts auswählen.',
        quickNotInCatalog: '#{{id}} (nicht mehr im Katalog)',
        quickCatalogTitle: 'Katalog',
        quickSearchPlaceholder: 'Suchen…',
        quickNoMatches: 'Keine Treffer.',
        quickAllSelected: 'Alle aktiven Einträge bereits ausgewählt.',
        quickVatShort: 'MwSt {{rate}}%',
        moveUp: 'Nach oben',
        moveDown: 'Nach unten',
        remove: 'Entfernen',
        // Save bar
        unsavedChanges: 'Ungespeicherte Änderungen',
        allSaved: 'Alle Änderungen gespeichert',
      },

      // ---- Onboarding tour ----
      onboarding: {
        headerTitle: 'SeaDent · Hilfe-Tour',
        skip: 'Überspringen',
        overview: 'Übersicht',
        back: 'Zurück',
        next: 'Weiter',
        finishTour: 'Tour abschließen',
        hubEyebrow: 'Hilfe — Erste Schritte',
        hubTitle: 'Belege schreiben in SeaDent',
        hubIntro:
          'Drei kurze Touren — Angebot, Rechnung und Stornorechnung. Jede dauert keine zwei Minuten. Du kannst sie in beliebiger Reihenfolge machen oder jederzeit anhalten und später fortfahren.',
        hubProgress: '{{done}}/{{total}} abgeschlossen',
        hubFooter1: 'Diese Hilfe findest du jederzeit unter',
        hubFooterLink: 'Hilfe → Belege',
        cardSteps: '{{count}} Schritte · {{mins}}',
        cardDone: 'fertig',
        cardContinue: 'fortsetzen',
        cardStart: 'starten',
        stepLabel: 'Schritt {{current}} von {{total}}',
        stepCount: '{{current}} / {{total}}',
        // Mock UI (illustrative)
        mock: {
          appointment: 'Termin',
          patient: 'Patient',
          treatment: 'Behandlung',
          edit: 'Bearbeiten',
          confirm: 'Bestätigen',
          cancel: 'Stornieren',
          createOffer: 'Angebot erstellen',
          newOfferDraft: 'Neues Angebot · Entwurf',
          validDays: 'gültig 30 Tage',
          draft: 'Entwurf',
          numberHint: 'Nummer (AN-…) wird beim',
          numberHintBold: 'Festschreiben',
          numberHintSuffix: 'vergeben — solange Entwurf, frei editierbar.',
          colPosition: 'Position',
          colQuantity: 'Menge',
          colUnit: 'Einzel',
          colVat: 'MwSt',
          colTotal: 'Gesamt',
          freeItem: 'Freie Position',
          fromCatalog: 'Aus Katalog',
          net: 'Netto',
          vatOn: 'MwSt 21 % auf 50 €',
          totalLabel: 'Total',
          finalize: 'Festschreiben',
          save: 'Speichern',
          finalized: 'festgeschrieben',
          validUntil: 'gültig bis 26.06.2026',
          patientAgreed: 'Patient hat zugestimmt — Status „Angenommen" gesetzt.',
          positionsCount: '3 Positionen',
          positionsList: 'Veneers oben (8), Lokalanästhesie (2), Wax-up',
          sendByMail: 'Per Mail senden',
          offerAccepted: 'Angebot angenommen',
          convertToInvoice: 'In Rechnung umwandeln',
          pdf: 'PDF',
          appointmentDetail: 'Termin-Detail',
          providerRoomInsurance: 'Behandler: Dr. García · Raum 1 · Versicherung: privat',
          completeTreatment: 'Behandlung abschließen',
          completeTreatmentHint:
            'Ein Klick öffnet den Schnell-Dialog mit Quick-Chips für die häufigsten Leistungen.',
          completeTreatmentTitle: 'Behandlung abschließen — Maria Pérez',
          quickDialog: 'Schnell-Dialog',
          quickSelection: 'Schnellauswahl',
          chipClickHint: 'Klick = Position hinzufügen · erneuter Klick erhöht die Menge.',
          searchCatalog: 'Aus Katalog suchen',
          totalLabelInline: 'Gesamt',
          asDraft: 'Als Entwurf',
          withoutInvoice: 'Ohne Rechnung',
          cancelBtn: 'Abbrechen',
          issueNow: 'Direkt ausstellen',
          documentPdf: 'Beleg-PDF',
          pdfPreview: 'PDF-Vorschau',
          total: 'Total',
          markPaid: 'Als bezahlt markieren',
          sendDialog: 'Versand-Dialog',
          sendDialogText:
            'An: maria.perez@example.com · Sprache: ES · Betreff & Anschreiben aus Sprach-Vorlage, direkt im Dialog editierbar.',
          finalizedNotEditable:
            'Festgeschrieben — der Beleg ist gesetzlich nicht mehr editierbar. Korrekturen ausschließlich über Stornierung.',
          cancelInvoiceTitle: 'Rechnung stornieren?',
          cancelInvoiceText:
            'Es wird automatisch eine Stornorechnung (ST-…) mit eigener Nummer und negativen Beträgen erzeugt. Die Originalrechnung erhält den Status „storniert".',
          reasonRequired: 'Grund (Pflicht)',
          reasonExample: 'Falscher Patient zugeordnet — Position wird auf RE-0144 neu ausgestellt.',
          documentsForPatient: 'Belege · Carlos Méndez',
          last30Days: 'Letzte 30 Tage',
          patientFile: 'Patientenakte',
          colNumber: 'Nummer',
          colDate: 'Datum',
          colStatus: 'Status',
          colAmount: 'Betrag',
          statusCanceled: 'storniert',
          cancellation: 'Stornorechnung',
          correctedInvoice: 'korrigierte Rechnung',
          numberRangeComplete: 'Nummernkreis bleibt lückenlos.',
          numberRangeCompleteText:
            'Original, Storno und Neu-Ausstellung tauchen alle drei in der Belegliste auf — so akzeptiert es das Finanzamt.',
        },
        // Chapters
        chapters: {
          offer: {
            tag: 'AN-…',
            title: 'Angebot erstellen',
            mins: '~2 min',
            blurb:
              'Kostenvoranschlag vor der Behandlung — versenden, vom Patienten bestätigen lassen, in eine Rechnung umwandeln.',
            step1Title: 'Vom zukünftigen Termin aus starten',
            step1Body:
              'Öffne den Termin im Kalender und klicke unten auf „Angebot erstellen". Der Editor übernimmt Patient, Sprache und Anschrift automatisch.',
            step1Tip: 'Tipp: Geht auch aus der Patientenakte heraus über „+ Beleg → Angebot".',
            step1Ann1: '„Angebot erstellen" öffnet den Editor mit allen Patientendaten vorbefüllt.',
            step2Title: 'Positionen erfassen und festschreiben',
            step2Body:
              'Trag die geplanten Leistungen ein — über „Aus Katalog" für Standards oder „Freie Position" für Sonderfälle. Mit Festschreiben bekommt das Angebot seine endgültige Nummer und das PDF wird erzeugt.',
            step2Tip:
              'Solange Entwurf, kannst du beliebig editieren oder verwerfen — keine Lücke in der Nummernreihe.',
            step2Ann1: 'Festschreiben vergibt die Nummer und sperrt den Beleg gegen Änderungen.',
            step3Title: 'Annehmen lassen → in Rechnung umwandeln',
            step3Body:
              'Versende das PDF per Mail. Sagt der Patient zu, klick auf „Angebot angenommen" und dann auf „In Rechnung umwandeln". Alle Positionen wandern in einen neuen Rechnungsentwurf.',
            step3Tip: 'Der Angebot-Verlauf bleibt am Termin sichtbar (Tab „Verlauf").',
            step3Ann1: '„In Rechnung umwandeln" — ein Klick, Positionen werden 1:1 übernommen.',
          },
          invoice: {
            tag: 'RE-…',
            title: 'Rechnung schreiben',
            mins: '~2 min',
            blurb:
              'Der Hauptpfad nach jeder Behandlung. Termin abschließen, Positionen festklicken, ausstellen, per Mail senden.',
            step1Title: 'Termin abschließen am Behandlungstag',
            step1Body:
              'Im Kalender den Termin öffnen → „Behandlung abschließen" klicken. Das ist der Schnell-Dialog — der einzige Knopf, den du im Alltag brauchst.',
            step1Tip: 'Aus der Appointments-Liste heraus geht es genauso — Link „Open in Calendar".',
            step1Ann1: 'Die Termin-Behandlung steht im Dialog schon als erste Position drin.',
            step2Title: 'Schnellauswahl-Chips & Katalog',
            step2Body:
              'Klick die Quick-Chips für häufige Leistungen — jeder Klick fügt eine Zeile hinzu (zweiter Klick erhöht die Menge). Spezielles holst du dir mit „Aus Katalog suchen", einmalige Leistungen über „Freie Position".',
            step2Tip:
              'Welche Chips erscheinen, stellst du in „Einstellungen → Rechnungsstellung → Schnellauswahl" ein (max. 8).',
            step2Ann1: 'Quick-Chip klicken = Position hinzufügen.',
            step2Ann2: 'Katalog für seltenere Leistungen · Freie Position für Sonderfälle.',
            step2Ann3:
              '„Direkt ausstellen" = Termin abschließen + Rechnung festschreiben + PDF — in einem Schritt.',
            step3Title: 'Per Mail senden',
            step3Body:
              'Nach „Direkt ausstellen" springt die Ansicht in den fertigen Beleg. Klick „Per Mail senden" — Empfänger und Sprache sind vorbefüllt, Betreff und Anschreiben kommen aus der Vorlage und sind im Dialog editierbar.',
            step3Tip: 'Variablen wie {{number}}, {{patient_name}}, {{total}} werden beim Versand ersetzt.',
            step3Ann1: 'Mail-Erfolg/-Fehler landet automatisch im Verlauf-Tab des Termins.',
            step3Ann2: 'Vorlage pro Sprache (DE/ES/EN) — pro Mail individuell anpassbar.',
          },
          cancellation: {
            tag: 'ST-…',
            title: 'Stornorechnung',
            mins: '~2 min',
            blurb:
              'Festgeschriebene Rechnungen lassen sich nicht editieren — Korrekturen laufen über die Storno. Klingt streng, ist aber Pflicht.',
            step1Title: 'Falsche Rechnung öffnen',
            step1Body:
              'Öffne die zu stornierende Rechnung. Sie ist gegen Änderungen gesperrt — ein gelber Hinweis erklärt das. Im Aktionspanel rechts findest du den Stornieren-Knopf.',
            step1Tip:
              'Eine festgeschriebene Rechnung wird nie gelöscht — der Originalbeleg bleibt für die Buchhaltung erhalten.',
            step1Ann1:
              '„Stornieren" startet die Korrektur. Der Originalbeleg bleibt unverändert bestehen.',
            step2Title: 'Grund eintragen',
            step2Body:
              'Schreib einen kurzen, ehrlichen Grund (z. B. „falscher Patient", „falscher MwSt-Satz", „Doppelt erfasst"). Der Grund landet im Audit-Log und auf dem Storno-PDF.',
            step2Tip: 'Pflichtfeld — ohne Grund lässt sich nicht stornieren.',
            step2Ann1: 'Der Grund erscheint später auf dem Storno-PDF und in der Belegliste.',
            step3Title: 'Storno + neue Rechnung',
            step3Body:
              'Beim Bestätigen entsteht automatisch eine Stornorechnung (ST-…) mit negativen Beträgen und eigener Nummer. Anschließend bei Bedarf eine korrigierte Rechnung neu ausstellen.',
            step3Tip: 'Alle drei Belege (Original, Storno, Korrektur) bleiben in der Belegliste sichtbar.',
            step3Ann1:
              'Das Finanzamt erwartet genau diese drei Zeilen. Nichts überschreiben, nichts löschen.',
          },
        },
      },
    },
  },
  es: {
    billing: {
      // Generic / shared
      eyebrow: 'Facturación',
      loading: 'Cargando…',
      loadError: 'No se pudieron cargar los documentos',
      saveFailed: 'Error al guardar',
      deleteFailed: 'Error al eliminar',
      sortFailed: 'Error al ordenar',
      actionFailed: 'Error en la acción',
      pdfLoadError: 'No se pudo cargar el PDF',
      filterReset: 'Restablecer filtros',
      noMatches: 'Sin resultados',
      back: 'Atrás',
      cancel: 'Cancelar',
      save: 'Guardar',
      saving: 'Guardando…',
      create: 'Crear',

      status: {
        draft: 'Borrador',
        sent: 'Enviado',
        accepted: 'Aceptado',
        rejected: 'Rechazado',
        issued: 'Emitido',
        paid: 'Pagado',
        canceled: 'Anulado',
      },

      type: {
        offer: 'Presupuesto',
        invoice: 'Factura',
        cancellation: 'Factura rectificativa',
      },

      lang: {
        de: 'Alemán',
        es: 'Español',
        en: 'Inglés',
        ru: 'Ruso',
      },

      category: {
        implantology: 'Implantología',
        prophylaxis: 'Profilaxis',
        aesthetic: 'Estética',
        oral_surgery: 'Cirugía',
        orthodontics: 'Ortodoncia',
        orthodonticsLong: 'Ortodoncia',
        consultation: 'Consulta',
        other: 'Otros',
      },

      list: {
        title: 'Documentos',
        description:
          'Presupuestos, facturas y rectificativas — numerados correlativamente tras su consolidación',
        countSuffix: 'documentos',
        open: 'pendientes',
        overdue: 'vencidos',
        catalog: 'Catálogo',
        catalogTitle: 'Gestionar el catálogo de prestaciones',
        offer: 'Presupuesto',
        invoice: 'Factura',
        tabAll: 'Todos',
        tabOffers: 'Presupuestos',
        tabInvoices: 'Facturas',
        tabCancellations: 'Rectificativas',
        tabTypesLabel: 'Tipos de documento',
        searchLabel: 'Buscar (número, paciente, ciudad)',
        searchPlaceholder: 'p. ej., RE-0042, Müller, Palma',
        statusLabel: 'Estado',
        statusAll: 'Todos',
        fromLabel: 'Desde',
        toLabel: 'Hasta',
        emptyTitle: 'Aún no hay documentos',
        emptyDescription:
          'Los documentos suelen crearse directamente desde una cita o desde el historial del paciente — o bien créelos arriba a la derecha.',
        emptyFilterDescription: 'Ningún documento coincide con los filtros actuales.',
        colNrType: 'Núm. / Tipo',
        colPatient: 'Paciente',
        colDate: 'Fecha',
        colStatus: 'Estado',
        colAmount: 'Importe',
        colAction: 'Acción',
        due: 'vence {{date}}',
        validUntil: 'válido hasta {{date}}',
        pdfOpen: 'Abrir PDF',
        openArrow: 'Abrir →',
        footerCount: '{{count}} documentos en el filtro actual',
        sum: 'Total:',
        helpTitle: '¿Cómo funcionan los documentos?',
        helpSubtitle:
          'Presupuesto, factura y factura rectificativa — explicados brevemente, con un recorrido interactivo.',
        startTour: 'Iniciar recorrido',
        helpCardOfferTitle: 'Presupuesto',
        helpCardOfferText:
          'Presupuesto previo al tratamiento — que el paciente lo acepte y, después, convertirlo en factura.',
        helpCardInvoiceTitle: 'Factura',
        helpCardInvoiceText:
          'Vía principal: finalizar la cita, pulsar los accesos rápidos, emitir y enviar por correo.',
        helpCardCancellationTitle: 'Factura rectificativa',
        helpCardCancellationText:
          'Correcciones de facturas consolidadas — flujo obligatorio para Hacienda.',
        helpTip: 'Consejo: puede volver a abrir el recorrido en cualquier momento mediante',
        helpTipBold: 'Iniciar recorrido',
        helpTipSuffix: 'arriba a la derecha.',
        helpMinutes: '~2 min',
      },

      catalog: {
        title: 'Catálogo de prestaciones',
        description:
          'Plantillas de posiciones reutilizables. Se ofrecen en el editor y en el diálogo «Finalizar tratamiento».',
        backToInvoices: 'Documentos',
        newItem: 'Nueva entrada',
        searchLabel: 'Buscar',
        searchPlaceholder: 'Código o denominación…',
        categoryLabel: 'Categoría',
        categoryAll: 'Todas',
        visibilityLabel: 'Visibilidad',
        visibilityActive: 'Solo activas',
        visibilityAll: 'Todas (incl. inactivas)',
        emptyTitle: 'Aún no hay entradas en el catálogo',
        emptyFilterTitle: 'Sin resultados',
        emptyDescription:
          'Cree una vez las prestaciones recurrentes — quedarán disponibles con un clic en el editor de documentos.',
        emptyFilterDescription: 'Ninguna entrada coincide con los filtros actuales.',
        emptyAction: 'Crear la primera entrada',
        colSort: 'Orden',
        colName: 'Denominación',
        colCodeCategory: 'Código · Categoría',
        colPriceVat: 'Precio · IVA',
        colVisible: 'Visible',
        colAction: 'Acción',
        moveUp: 'Subir',
        moveDown: 'Bajar',
        unnamed: 'sin nombre',
        vatShort: 'IVA {{rate}}%',
        active: 'activa',
        inactive: 'inactiva',
        loadError: 'No se pudo cargar el catálogo',
        itemUpdated: 'Entrada del catálogo actualizada',
        itemCreated: 'Entrada del catálogo creada',
        itemDeleted: 'Entrada eliminada',
        confirmDelete:
          '¿Eliminar realmente la entrada «{{name}}»? Los documentos existentes no se verán afectados.',
        editorEditTitle: 'Editar entrada del catálogo',
        editorNewTitle: 'Nueva entrada del catálogo',
        codeLabel: 'Código (interno, opcional)',
        codePlaceholder: 'p. ej., IMP-STR',
        statusLabel: 'Estado',
        statusActive: 'Activa',
        statusInactive: 'Inactiva (no aparece en el selector)',
        nameSection: 'Denominación (al menos un idioma)',
        descriptionSection: 'Descripción (opcional)',
        unitLabel: 'Unidad',
        priceLabel: 'Precio (predeterminado)',
        vatLabel: 'IVA % (predeterminado)',
        sortOrderLabel: 'Orden',
      },

      picker: {
        title: 'Buscar en el catálogo',
        categoryAll: 'Todas las categorías',
        loading: 'Cargando catálogo…',
        empty:
          'El catálogo está vacío — cree primero entradas (Configuración → Facturación → Catálogo).',
        noMatches: 'Sin resultados. Pruebe otra palabra clave o cree una «Posición libre».',
        unnamed: 'Sin nombre',
        vatShort: 'IVA {{rate}}%',
        searchPlaceholder: 'Código o denominación…',
      },

      editor: {
        loadError: 'No se pudo cargar el documento',
        createError: 'No se pudo crear el documento. Seleccione de nuevo el paciente.',
        patientNotFound:
          'No se encontró el paciente con ID {{id}}. Seleccione otro o cree uno nuevo.',
        saved: 'Documento guardado',
        itemAddError: 'No se pudo añadir la posición',
        itemSaveError: 'No se pudo guardar la posición',
        freeItemLabel: 'Posición libre',
        confirmDeleteItem: '¿Eliminar la posición «{{label}}»?',
        itemWithoutLabel: 'sin denominación',
        confirmFinalizeDirty: 'Hay cambios sin guardar en el documento. ¿Consolidar de todos modos?',
        finalizedAs: 'Consolidado como {{number}}',
        markedPaid: 'Marcado como pagado',
        cancelReasonPrompt: 'Motivo de la rectificación (constará en la factura rectificativa):',
        cancellationCreated: 'Factura rectificativa {{number}} creada',
        invoiceDraftCreated: 'Borrador de factura creado',
        confirmDiscardDraft: '¿Descartar realmente este borrador? Esta acción no se puede deshacer.',
        draftDeleted: 'Borrador eliminado',
        notFound: 'Documento no encontrado.',
        newDocTitle: 'Nuevo documento ({{type}})',
        newDocDescription:
          'Seleccione primero el paciente — la dirección y el idioma se aplicarán automáticamente.',
        patientLabel: 'Paciente',
        pickPatientTip:
          'Consejo: si el paciente buscado no aparece, créelo directamente en el desplegable de abajo — la dirección y el idioma se pueden completar más tarde.',
        finalizedDescription:
          'Consolidado el {{date}} — los cambios solo mediante factura rectificativa.',
        editorDescription:
          'Editor — posiciones, dirección y textos. Guardar confirma los cambios; consolidar asigna el número.',
        pdf: 'PDF',
        preview: 'Vista previa',
        listLink: 'Lista',
        finalizedBannerTitle: 'El documento está consolidado',
        finalizedBannerText:
          'Las entradas están bloqueadas. Si es necesario, genere una factura rectificativa y vuelva a emitir.',
        viewOnlyBannerTitle: 'Solo lectura',
        viewOnlyBannerText:
          'Puede ver este documento, pero no editarlo. Le falta el permiso «Crear/editar facturas».',
        recipientTitle: 'Destinatario (instantánea)',
        nameCompany: 'Nombre / Empresa',
        nieNif: 'NIE / NIF / N.º de IVA',
        street: 'Calle',
        postalCode: 'C.P.',
        country: 'País',
        city: 'Ciudad',
        email: 'Correo electrónico',
        recipientHint:
          'La dirección se congela en la factura. Los cambios aquí no afectan a los datos maestros del paciente.',
        headerNoteLabel: 'Texto de encabezado (sobre la tabla)',
        headerNotePlaceholder: 'p. ej., "Tratamiento del 13.05.2026"',
        itemsTitle: 'Posiciones ({{count}})',
        fromCatalog: 'Desde el catálogo',
        freeItem: 'Posición libre',
        itemsEmptyTitle: 'Aún no hay posiciones',
        itemsEmptyReadonly: 'Documento consolidado sin posiciones — no editable.',
        itemsEmptyEditable: 'Elija una plantilla del catálogo o añada una posición libre.',
        colNr: '#',
        colLabel: 'Denominación',
        colQuantity: 'Cantidad',
        colUnit: 'Unidad',
        colUnitPrice: 'Precio unitario',
        colVat: 'IVA %',
        colDiscount: 'Dto. %',
        colTotal: 'Total',
        labelPlaceholder: 'Denominación de la prestación',
        descriptionPlaceholder: 'Texto adicional opcional',
        moveUp: 'Subir',
        moveDown: 'Bajar',
        moveItemUp: 'Mover la posición hacia arriba',
        moveItemDown: 'Mover la posición hacia abajo',
        deleteItem: 'Eliminar posición',
        paymentTerms: 'Condiciones de pago',
        bankDetails: 'Datos bancarios (sobrescribe la configuración)',
        bankDetailsPlaceholder: 'Vacío = texto estándar de la configuración',
        footerText: 'Texto de pie (p. ej., «Muchas gracias…»)',
        internalNote: 'Nota interna (no visible en la factura)',
        metaTitle: 'Datos maestros',
        patientFile: 'Historial →',
        language: 'Idioma',
        date: 'Fecha',
        invoiceDate: 'Fecha de factura',
        dueDate: 'Vence el',
        validUntilLabel: 'Válido hasta',
        createdFromOffer: 'Generado a partir de',
        offerHash: 'Presupuesto #{{id}}',
        cancelsInvoice: 'Rectifica',
        invoiceHash: 'Factura #{{id}}',
        canceledBy: 'Rectificada por',
        cancellationHash: 'Factura rectificativa #{{id}}',
        totalsTitle: 'Totales',
        net: 'Base imponible',
        vatRate: 'IVA {{rate}}%',
        total: 'Total',
        saveChanges: 'Guardar cambios',
        savedLabel: 'Guardado',
        savingLabel: 'Guardando…',
        finalize: 'Consolidar',
        finalizing: 'Consolidando…',
        finalizeDisabledTitle: 'Se requiere al menos una posición',
        discardDraft: 'Descartar borrador',
        offerAccepted: 'Presupuesto aceptado',
        offerRejected: 'Rechazado',
        convertToInvoice: 'Convertir en factura',
        markPaid: 'Marcar como pagado',
        cancelInvoice: 'Rectificar',
        sendByMail: 'Enviar por correo',
        downloadPdf: 'Descargar PDF',
      },

      completion: {
        prepareError: 'Error en la preparación',
        title: 'Finalizar tratamiento',
        seedItemLabel: 'Tratamiento',
        seedItemUnit: 'Sesión',
        patientLinked: 'Paciente {{name}} vinculado',
        linkError: 'Error al vincular',
        noPatientError: 'Cita sin paciente vinculado — vincule primero un paciente.',
        noItemsError: 'Se requiere al menos una posición con denominación.',
        treatmentFrom: 'Tratamiento del {{date}}.',
        completedNoInvoice: 'Cita finalizada (sin factura)',
        draftCreated: 'Borrador de factura creado',
        invoiceIssued: 'Factura {{number}} emitida',
        noPatientTitle: 'Ningún paciente vinculado',
        noPatientText:
          'Para poder crear una factura, la cita debe estar asociada a un registro de paciente. Como alternativa, puede finalizar la cita sin factura.',
        linkPatient: 'Vincular paciente',
        patientSearchPlaceholder: 'Nombre, correo, teléfono o número de paciente…',
        searching: 'Buscando…',
        noMatches: 'Sin resultados.',
        linking: 'Vinculando…',
        patientNotListed: '¿No está el paciente?',
        createInFile: 'Créelo en el historial del paciente',
        createInFileSuffix: 'y vuelva a finalizar después.',
        servicesTitle: 'Prestaciones realizadas ({{count}})',
        itemsEmpty: 'Aún no hay posiciones. Elija abajo del catálogo o cree una posición libre.',
        colLabel: 'Denominación',
        colQuantity: 'Cant.',
        colUnitPrice: 'Precio unitario',
        colVat: 'IVA%',
        colTotal: 'Total',
        servicePlaceholder: 'Prestación',
        removeItem: 'Quitar posición',
        quickTitle: 'Prestaciones frecuentes (1 clic = añadir)',
        quickHint:
          'Consejo: en Configuración → Facturación → Acceso rápido puede definir hasta 8 prestaciones frecuentes como botones de 1 clic.',
        searchCatalog: 'Buscar en el catálogo',
        freeItem: 'Posición libre',
        catalogSearchPlaceholder: 'Código o denominación…',
        noMatchesCatalog: 'Sin resultados.',
        internalNoteLabel: 'Nota (interna, no visible en la factura)',
        internalNotePlaceholder: 'p. ej., el paciente desea ajustar la corona en 4 semanas',
        languageLabel: 'Idioma:',
        appointmentStatus: 'Estado de la cita:',
        net: 'Base imponible',
        vat: 'IVA',
        withoutInvoice: 'Sin factura',
        asDraft: 'Como borrador',
        issueNow: 'Emitir directamente',
        savingDraft: 'Guardando…',
        issuing: 'Emitiendo…',
        patientRequired: 'Debe haber un paciente vinculado',
        itemLabelRequired: 'Se requiere al menos una posición con denominación',
      },

      send: {
        previewError: 'No se pudo cargar la plantilla',
        recipientSubjectRequired: 'El destinatario y el asunto son obligatorios',
        sent: 'Correo enviado',
        sendFailed: 'Error en el envío',
        title: 'Enviar documento por correo',
        loadingTemplate: 'Cargando plantilla…',
        toLabel: 'Para',
        toPlaceholder: 'paciente@ejemplo.com',
        languageLabel: 'Idioma',
        subjectLabel: 'Asunto',
        bodyLabel: 'Mensaje',
        attachment: 'Adjunto:',
        attachmentNote: '(original congelado)',
        reloadTemplate: 'Recargar plantilla',
        send: 'Enviar',
        sending: 'Enviando…',
      },

      patientPicker: {
        change: 'Cambiar',
        cancel: 'Cancelar',
        searchPlaceholder: 'Buscar paciente (nombre, correo, NIE, teléfono)…',
        minChars: 'Introduzca al menos 2 caracteres o cree un nuevo paciente.',
        searching: 'Buscando…',
        noMatches: 'Sin resultados para «{{query}}».',
        createNew: 'Crear nuevo paciente',
        newPatient: 'Nuevo paciente',
        firstName: 'Nombre',
        lastName: 'Apellidos',
        emailLabel: 'Correo electrónico',
        phoneLabel: 'Teléfono',
        languageLabel: 'Idioma',
        optional: 'opcional',
        patientCreated: 'Paciente {{number}} creado',
        createError: 'No se pudo crear el paciente',
        addressLaterHint:
          'La dirección y el NIE se pueden añadir más tarde en el historial del paciente.',
        createAndUse: 'Crear y usar',
        creating: 'Creando…',
      },

      settings: {
        loadError: 'No se pudo cargar la configuración',
        saved: 'Configuración guardada',
        smtpTitle: 'Cuenta de envío para facturas',
        smtpDescription:
          'Buzón propio para documentos (predeterminado billing@seadent.es) — separado del buzón info@ de las reservas. Garantiza que las respuestas de los pacientes lleguen directamente al buzón de contabilidad.',
        smtpSaved: 'Cuenta de envío guardada',
        smtpRecipientMissing: 'Falta el destinatario',
        smtpTestSent: 'Correo de prueba enviado a {{to}}',
        smtpTestFailed: 'Error en la prueba',
        smtpPassSet: 'Contraseña guardada. Envío listo.',
        smtpPassUnset:
          'Aún no se ha establecido contraseña — introdúzcala abajo y guarde. Solo entonces funcionará «Enviar por correo».',
        smtpDisplayName: 'Nombre visible (remitente)',
        smtpDisplayNamePlaceholder: 'SeaDent Mallorca · Billing',
        smtpEmail: 'Dirección de correo',
        smtpHost: 'Servidor SMTP',
        smtpPort: 'Puerto',
        smtpConnection: 'Conexión',
        smtpStarttls: 'STARTTLS (puerto 587)',
        smtpTls: 'TLS implícito (puerto 465)',
        smtpPassword: 'Contraseña',
        smtpPasswordKeepHint: '(deje en blanco para conservar la contraseña actual)',
        smtpPasswordPlaceholder: 'Contraseña del proveedor de hosting',
        smtpSaveAccount: 'Guardar cuenta',
        smtpTestPlaceholder: 'prueba@direccion.com',
        smtpTest: 'Prueba',
        smtpTesting: 'Enviando…',
        smtpTestNeedsPass: 'Guarde primero la contraseña',
        issuerTitle: 'Datos del remitente de la clínica',
        issuerDescription:
          'Estos datos aparecen en la parte superior de cada documento. Campos obligatorios para facturas DACH/ES: empresa, NIF/N.º de IVA, dirección.',
        issuerName: 'Razón social (incl. forma jurídica)',
        issuerNamePlaceholder: 'p. ej., SeaDent Mallorca SL',
        issuerLegalForm: 'Forma jurídica (opcional)',
        issuerLegalFormPlaceholder: 'SL / GmbH / …',
        issuerNif: 'NIF (España)',
        issuerVatId: 'N.º de IVA (adicional, opcional)',
        issuerMedicalDirector: 'Director médico (opcional)',
        issuerAddress1: 'Línea de dirección 1',
        issuerAddress2: 'Línea de dirección 2 (opcional)',
        issuerPostalCode: 'C.P.',
        issuerCity: 'Ciudad',
        issuerCountry: 'País (ISO 2)',
        issuerPhone: 'Teléfono',
        issuerEmail: 'Correo electrónico',
        issuerWebsite: 'Sitio web (opcional)',
        issuerBank: 'Banco',
        issuerIban: 'IBAN',
        issuerBic: 'BIC',
        defaultsTitle: 'Valores predeterminados para nuevos documentos',
        defaultsDescription: 'Se rellenan al crear — sobrescribibles por documento.',
        defaultVat: 'IVA predeterminado',
        defaultDueDays: 'Plazo de pago (días)',
        defaultOfferValidDays: 'Presupuesto válido (días)',
        defaultLanguage: 'Idioma predeterminado',
        defaultCurrency: 'Moneda (ISO)',
        countersTitle: 'Series de numeración',
        countersDescription:
          'Series correlativas y separadas. El número siguiente puede establecerse en la migración inicial desde el sistema anterior — después es mejor no modificarlo.',
        counterUpdated: 'Serie de numeración {{type}} actualizada',
        counterUpdateError: 'No se pudo modificar la serie de numeración',
        counterColType: 'Tipo',
        counterColPrefix: 'Prefijo',
        counterColPadding: 'Relleno',
        counterColNext: 'Siguiente núm.',
        counterColPreview: 'Vista previa',
        textsTitle: 'Textos estándar por idioma',
        textsDescription: 'Se rellenan en los nuevos documentos. Sobrescribibles por documento.',
        textPaymentTerms: 'Condiciones de pago',
        textPaymentTermsHint: 'Aparece bajo los totales en las facturas.',
        textBankDetails: 'Datos bancarios (sobrescribir)',
        textBankDetailsHint: 'Vacío = generar automáticamente desde los datos del remitente.',
        textDefaultFooter: 'Texto de pie estándar',
        textDefaultFooterHint:
          'Propuesta para el texto de despedida amable al final de cada factura — editable por documento.',
        textVatExemption: 'Aviso de exención de IVA',
        textVatExemptionHint: 'Se muestra en cuanto una posición tiene un 0 % de IVA.',
        emailTitle: 'Mensajes de correo para presupuestos y facturas',
        emailDescription:
          'Estos textos se rellenan al enviar. El texto se puede ajustar por documento en el diálogo "Enviar por correo". Variables disponibles: {{number}}, {{patient_name}}, {{total}}, {{due_date}}, {{valid_until}}, {{issuer_name}}.',
        emailSubject: 'Asunto',
        emailBody: 'Mensaje',
        quickTitle: 'Acceso rápido en el diálogo «Finalizar tratamiento»',
        quickDescription:
          'Hasta 8 entradas del catálogo — se muestran en el flujo de la cita como botones de 1 clic para registrar prestaciones frecuentes al instante.',
        quickCatalogEmpty: 'El catálogo está vacío. Cree primero entradas —',
        quickOpenCatalog: 'Abrir catálogo',
        quickSelection: 'Selección ({{count}}/{{max}})',
        quickNothingSelected: 'Aún no se ha seleccionado nada. Seleccione a la derecha.',
        quickNotInCatalog: '#{{id}} (ya no está en el catálogo)',
        quickCatalogTitle: 'Catálogo',
        quickSearchPlaceholder: 'Buscar…',
        quickNoMatches: 'Sin resultados.',
        quickAllSelected: 'Todas las entradas activas ya están seleccionadas.',
        quickVatShort: 'IVA {{rate}}%',
        moveUp: 'Subir',
        moveDown: 'Bajar',
        remove: 'Quitar',
        unsavedChanges: 'Cambios sin guardar',
        allSaved: 'Todos los cambios guardados',
      },

      onboarding: {
        headerTitle: 'SeaDent · Recorrido de ayuda',
        skip: 'Omitir',
        overview: 'Resumen',
        back: 'Atrás',
        next: 'Siguiente',
        finishTour: 'Finalizar recorrido',
        hubEyebrow: 'Ayuda — Primeros pasos',
        hubTitle: 'Crear documentos en SeaDent',
        hubIntro:
          'Tres recorridos breves — presupuesto, factura y factura rectificativa. Cada uno dura menos de dos minutos. Puede hacerlos en cualquier orden o detenerse y continuar más tarde.',
        hubProgress: '{{done}}/{{total}} completados',
        hubFooter1: 'Esta ayuda está disponible en cualquier momento en',
        hubFooterLink: 'Ayuda → Documentos',
        cardSteps: '{{count}} pasos · {{mins}}',
        cardDone: 'hecho',
        cardContinue: 'continuar',
        cardStart: 'empezar',
        stepLabel: 'Paso {{current}} de {{total}}',
        stepCount: '{{current}} / {{total}}',
        mock: {
          appointment: 'Cita',
          patient: 'Paciente',
          treatment: 'Tratamiento',
          edit: 'Editar',
          confirm: 'Confirmar',
          cancel: 'Cancelar',
          createOffer: 'Crear presupuesto',
          newOfferDraft: 'Nuevo presupuesto · Borrador',
          validDays: 'válido 30 días',
          draft: 'Borrador',
          numberHint: 'El número (AN-…) se asigna al',
          numberHintBold: 'consolidar',
          numberHintSuffix: ' — mientras es borrador, es libremente editable.',
          colPosition: 'Posición',
          colQuantity: 'Cantidad',
          colUnit: 'Unitario',
          colVat: 'IVA',
          colTotal: 'Total',
          freeItem: 'Posición libre',
          fromCatalog: 'Desde el catálogo',
          net: 'Base imponible',
          vatOn: 'IVA 21 % sobre 50 €',
          totalLabel: 'Total',
          finalize: 'Consolidar',
          save: 'Guardar',
          finalized: 'consolidado',
          validUntil: 'válido hasta 26.06.2026',
          patientAgreed: 'El paciente ha dado su conformidad — estado «Aceptado».',
          positionsCount: '3 posiciones',
          positionsList: 'Carillas superiores (8), anestesia local (2), encerado',
          sendByMail: 'Enviar por correo',
          offerAccepted: 'Presupuesto aceptado',
          convertToInvoice: 'Convertir en factura',
          pdf: 'PDF',
          appointmentDetail: 'Detalle de la cita',
          providerRoomInsurance: 'Doctor: Dr. García · Sala 1 · Seguro: privado',
          completeTreatment: 'Finalizar tratamiento',
          completeTreatmentHint:
            'Un clic abre el diálogo rápido con accesos rápidos para las prestaciones más frecuentes.',
          completeTreatmentTitle: 'Finalizar tratamiento — Maria Pérez',
          quickDialog: 'Diálogo rápido',
          quickSelection: 'Acceso rápido',
          chipClickHint: 'Clic = añadir posición · otro clic aumenta la cantidad.',
          searchCatalog: 'Buscar en el catálogo',
          totalLabelInline: 'Total',
          asDraft: 'Como borrador',
          withoutInvoice: 'Sin factura',
          cancelBtn: 'Cancelar',
          issueNow: 'Emitir directamente',
          documentPdf: 'PDF del documento',
          pdfPreview: 'Vista previa del PDF',
          total: 'Total',
          markPaid: 'Marcar como pagado',
          sendDialog: 'Diálogo de envío',
          sendDialogText:
            'Para: maria.perez@example.com · Idioma: ES · Asunto y mensaje desde la plantilla de idioma, editables directamente en el diálogo.',
          finalizedNotEditable:
            'Consolidado — el documento ya no es editable legalmente. Las correcciones solo mediante rectificación.',
          cancelInvoiceTitle: '¿Rectificar la factura?',
          cancelInvoiceText:
            'Se genera automáticamente una factura rectificativa (ST-…) con número propio e importes negativos. La factura original pasa al estado «anulada».',
          reasonRequired: 'Motivo (obligatorio)',
          reasonExample: 'Paciente asignado incorrectamente — la posición se reemite en RE-0144.',
          documentsForPatient: 'Documentos · Carlos Méndez',
          last30Days: 'Últimos 30 días',
          patientFile: 'Historial del paciente',
          colNumber: 'Número',
          colDate: 'Fecha',
          colStatus: 'Estado',
          colAmount: 'Importe',
          statusCanceled: 'anulada',
          cancellation: 'Factura rectificativa',
          correctedInvoice: 'factura corregida',
          numberRangeComplete: 'La serie de numeración permanece sin huecos.',
          numberRangeCompleteText:
            'El original, la rectificativa y la reemisión aparecen los tres en la lista de documentos — así lo acepta Hacienda.',
        },
        chapters: {
          offer: {
            tag: 'AN-…',
            title: 'Crear presupuesto',
            mins: '~2 min',
            blurb:
              'Presupuesto previo al tratamiento — enviarlo, que el paciente lo confirme, convertirlo en factura.',
            step1Title: 'Empezar desde la futura cita',
            step1Body:
              'Abra la cita en el calendario y pulse abajo en «Crear presupuesto». El editor toma automáticamente el paciente, el idioma y la dirección.',
            step1Tip: 'Consejo: también funciona desde el historial del paciente mediante «+ Documento → Presupuesto».',
            step1Ann1: '«Crear presupuesto» abre el editor con todos los datos del paciente rellenados.',
            step2Title: 'Registrar posiciones y consolidar',
            step2Body:
              'Introduzca las prestaciones previstas — con «Desde el catálogo» para estándares o «Posición libre» para casos especiales. Al consolidar, el presupuesto obtiene su número definitivo y se genera el PDF.',
            step2Tip:
              'Mientras sea borrador, puede editar o descartar libremente — sin huecos en la serie de numeración.',
            step2Ann1: 'Consolidar asigna el número y bloquea el documento contra cambios.',
            step3Title: 'Hacer que lo acepten → convertir en factura',
            step3Body:
              'Envíe el PDF por correo. Si el paciente acepta, pulse «Presupuesto aceptado» y luego «Convertir en factura». Todas las posiciones pasan a un nuevo borrador de factura.',
            step3Tip: 'El historial del presupuesto permanece visible en la cita (pestaña «Historial»).',
            step3Ann1: '«Convertir en factura» — un clic, las posiciones se copian 1:1.',
          },
          invoice: {
            tag: 'RE-…',
            title: 'Crear factura',
            mins: '~2 min',
            blurb:
              'La vía principal tras cada tratamiento. Finalizar la cita, fijar las posiciones, emitir y enviar por correo.',
            step1Title: 'Finalizar la cita el día del tratamiento',
            step1Body:
              'Abra la cita en el calendario → pulse «Finalizar tratamiento». Es el diálogo rápido — el único botón que necesita en el día a día.',
            step1Tip: 'Desde la lista de citas funciona igual — enlace «Open in Calendar».',
            step1Ann1: 'El tratamiento de la cita ya consta como primera posición en el diálogo.',
            step2Title: 'Accesos rápidos y catálogo',
            step2Body:
              'Pulse los accesos rápidos para prestaciones frecuentes — cada clic añade una línea (un segundo clic aumenta la cantidad). Lo especial se busca con «Buscar en el catálogo»; las prestaciones puntuales con «Posición libre».',
            step2Tip:
              'Qué accesos aparecen se configura en «Configuración → Facturación → Acceso rápido» (máx. 8).',
            step2Ann1: 'Pulsar un acceso rápido = añadir posición.',
            step2Ann2: 'Catálogo para prestaciones menos frecuentes · Posición libre para casos especiales.',
            step2Ann3:
              '«Emitir directamente» = finalizar la cita + consolidar la factura + PDF — en un solo paso.',
            step3Title: 'Enviar por correo',
            step3Body:
              'Tras «Emitir directamente», la vista salta al documento finalizado. Pulse «Enviar por correo» — el destinatario y el idioma están rellenados, el asunto y el mensaje provienen de la plantilla y son editables en el diálogo.',
            step3Tip: 'Variables como {{number}}, {{patient_name}}, {{total}} se sustituyen al enviar.',
            step3Ann1: 'El éxito/error del correo se registra automáticamente en la pestaña Historial de la cita.',
            step3Ann2: 'Plantilla por idioma (DE/ES/EN) — personalizable por correo.',
          },
          cancellation: {
            tag: 'ST-…',
            title: 'Factura rectificativa',
            mins: '~2 min',
            blurb:
              'Las facturas consolidadas no se pueden editar — las correcciones van por la rectificativa. Suena estricto, pero es obligatorio.',
            step1Title: 'Abrir la factura errónea',
            step1Body:
              'Abra la factura que desea rectificar. Está bloqueada contra cambios — un aviso amarillo lo explica. En el panel de acciones a la derecha encontrará el botón Rectificar.',
            step1Tip:
              'Una factura consolidada nunca se elimina — el documento original se conserva para la contabilidad.',
            step1Ann1: '«Rectificar» inicia la corrección. El documento original permanece sin cambios.',
            step2Title: 'Introducir el motivo',
            step2Body:
              'Escriba un motivo breve y honesto (p. ej., «paciente incorrecto», «tipo de IVA incorrecto», «registrado por duplicado»). El motivo se registra en el log de auditoría y en el PDF de la rectificativa.',
            step2Tip: 'Campo obligatorio — sin motivo no se puede rectificar.',
            step2Ann1: 'El motivo aparece después en el PDF de la rectificativa y en la lista de documentos.',
            step3Title: 'Rectificativa + nueva factura',
            step3Body:
              'Al confirmar se genera automáticamente una factura rectificativa (ST-…) con importes negativos y número propio. Después, si es necesario, vuelva a emitir una factura corregida.',
            step3Tip: 'Los tres documentos (original, rectificativa, corrección) permanecen visibles en la lista.',
            step3Ann1: 'Hacienda espera exactamente estas tres líneas. No sobrescribir, no eliminar.',
          },
        },
      },
    },
  },
};
